// ============================================================================
// KLINISOL CRO - Contact Page (REDESIGNED)
// Matches Home page design system with full form functionality
// BOT PROTECTION: Google reCAPTCHA v2
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { 
    Mail, 
    Phone, 
    MapPin, 
    Clock,
    Heart,
    Activity,
    Stethoscope,
    CheckCircle,
    AlertCircle,
    Send
} from 'lucide-react';

// reCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = '6LcLgRosAAAAAGY351DknAOk8TdNgCznmoCfLo8A';

const Contact = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organization: '',
        subject: 'general',
        message: ''
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const recaptchaRef = useRef(null);

    // Load reCAPTCHA script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        // Define callback for reCAPTCHA
        window.onRecaptchaChange = (token) => {
            setRecaptchaToken(token);
            if (error === 'Please complete the reCAPTCHA verification') {
                setError('');
            }
        };

        window.onRecaptchaExpired = () => {
            setRecaptchaToken('');
        };

        return () => {
            // Cleanup
            delete window.onRecaptchaChange;
            delete window.onRecaptchaExpired;
        };
    }, [error]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
    };

    const validateForm = () => {
        if (!formData.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }
        if (!formData.message.trim()) {
            setError('Message is required');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        if (!recaptchaToken) {
            setError('Please complete the reCAPTCHA verification');
            return false;
        }

        return true;
    };

    const resetRecaptcha = () => {
        if (window.grecaptcha) {
            window.grecaptcha.reset();
        }
        setRecaptchaToken('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/contact/submit', {
                ...formData,
                recaptchaToken
            });

            if (response.data.success) {
                setSuccess(true);
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    organization: '',
                    subject: 'general',
                    message: ''
                });
                resetRecaptcha();
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (err) {
            console.error('Contact form error:', err);
            setError(err.response?.data?.message || 'Failed to send message. Please try again.');
            resetRecaptcha();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Page Header */}
            <section className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        Let's discuss how KLINiSOL can support your clinical research needs
                    </p>
                </div>
            </section>

            {/* Contact Content */}
            <section className="py-16 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Contact Information */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-6">
                                <h2 className="text-2xl font-bold text-[#475569] mb-4">Get in Touch</h2>
                                <p className="text-gray-700 mb-6 leading-relaxed">
                                    Our team of clinical research experts is ready to discuss your 
                                    investigator-initiated trial needs. Contact us today for a free consultation.
                                </p>

                                <div className="space-y-6">
                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-[#E91E63]/10 rounded-lg flex items-center justify-center">
                                                <Mail className="w-6 h-6 text-[#E91E63]" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                                            <p className="text-gray-600 text-sm">info@klinisol.com</p>
                                            <p className="text-gray-600 text-sm">support@klinisol.com</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-[#E91E63]/10 rounded-lg flex items-center justify-center">
                                                <Phone className="w-6 h-6 text-[#E91E63]" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                                            <p className="text-gray-600 text-sm">Available upon request</p>
                                            <p className="text-gray-600 text-sm">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-[#E91E63]/10 rounded-lg flex items-center justify-center">
                                                <MapPin className="w-6 h-6 text-[#E91E63]" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">Office</h4>
                                            <p className="text-gray-600 text-sm">600 Broadway Ste 200 #2189</p>
                                            <p className="text-gray-600 text-sm">Albany, NY 12207, United States</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-[#E91E63]/10 rounded-lg flex items-center justify-center">
                                                <Clock className="w-6 h-6 text-[#E91E63]" />
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">Response Time</h4>
                                            <p className="text-gray-600 text-sm">We typically respond within 24 hours</p>
                                            <p className="text-gray-600 text-sm">Emergency inquiries: Within 2 hours</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/5 rounded-xl border-2 border-[#E91E63]/30 p-6 hover:shadow-lg hover:border-[#E91E63] transition-all">
                                <h4 className="font-bold text-gray-900 mb-4">Our Therapeutic Specialties</h4>
                                <ul className="space-y-3">
                                    {[
                                        { icon: Activity, text: "Dermatology Research" },
                                        { icon: Heart, text: "Cardiovascular Research" },
                                        { icon: Stethoscope, text: "Gastroenterology Research" }
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-center gap-3 text-gray-700">
                                            <item.icon className="w-5 h-5 text-[#E91E63]" />
                                            <span>{item.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                                <h2 className="text-2xl font-bold text-[#475569] mb-6">Send us a Message</h2>

                                {success && (
                                    <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-green-900">Message Sent Successfully!</p>
                                            <p className="text-sm text-green-700 mt-1">Thank you for contacting KLIN<span className="text-[#E91E63]">i</span>SOL. We'll get back to you within 24 hours.</p>
                                        </div>
                                    </div>
                                )}

                                {error && (
                                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                                        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-red-900">Error</p>
                                            <p className="text-sm text-red-700 mt-1">{error}</p>
                                        </div>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                                                First Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="firstName"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                placeholder="John"
                                                disabled={loading}
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="lastName"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                placeholder="Doe"
                                                disabled={loading}
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="john.doe@example.com"
                                                disabled={loading}
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+1 (555) 000-0000"
                                                disabled={loading}
                                                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="organization" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Organization
                                        </label>
                                        <input
                                            type="text"
                                            id="organization"
                                            name="organization"
                                            value={formData.organization}
                                            onChange={handleChange}
                                            placeholder="Your Institution or Company"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Subject
                                        </label>
                                        <select
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <option value="general">General Inquiry</option>
                                            <option value="new_trial">New Trial Consultation</option>
                                            <option value="services">Services Information</option>
                                            <option value="partnership">Partnership Opportunity</option>
                                            <option value="support">Technical Support</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                                            Message <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Tell us about your clinical trial needs..."
                                            rows="6"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed resize-vertical"
                                            required
                                        ></textarea>
                                    </div>

                                    {/* reCAPTCHA Widget */}
                                    <div>
                                        <div 
                                            className="g-recaptcha" 
                                            data-sitekey={RECAPTCHA_SITE_KEY}
                                            data-callback="onRecaptchaChange"
                                            data-expired-callback="onRecaptchaExpired"
                                            ref={recaptchaRef}
                                        ></div>
                                    </div>

                                    <div>
                                        <button 
                                            type="submit" 
                                            disabled={loading}
                                            className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {loading ? 'Sending...' : 'Send Message'}
                                            {!loading && <Send className="w-5 h-5" />}
                                        </button>
                                        <p className="text-sm text-gray-600 mt-3">
                                            <span className="text-red-500">*</span> Required fields
                                        </p>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-2xl font-bold mb-2">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h3>
                            <p className="text-sm opacity-90">Advancing Investigator-Initiated Research</p>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/" className="hover:text-[#E91E63] transition-colors">Home</Link></li>
                                <li><Link to="/services" className="hover:text-[#E91E63] transition-colors">Services</Link></li>
                                <li><Link to="/therapeutic-areas" className="hover:text-[#E91E63] transition-colors">Therapeutic Areas</Link></li>
                                <li><Link to="/about" className="hover:text-[#E91E63] transition-colors">About Us</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">For Team Members</h4>
                            <ul className="space-y-2 text-sm">
                                <li><Link to="/dashboard" className="hover:text-[#E91E63] transition-colors">Training Portal</Link></li>
                                <li><Link to="/dashboard" className="hover:text-[#E91E63] transition-colors">SOP Library</Link></li>
                                <li><Link to="/login" className="hover:text-[#E91E63] transition-colors">Login</Link></li>
                            </ul>
                        </div>
                        
                        <div>
                            <h4 className="font-semibold mb-3">Contact</h4>
                            <p className="text-sm opacity-90 mb-1">600 Broadway Ste 200 #2189</p>
                            <p className="text-sm opacity-90 mb-2">Albany, NY 12207</p>
                            <p className="text-sm opacity-90 mb-1">Email: info@klinisol.com</p>
                            <p className="text-sm opacity-90">Website: klinisol.com</p>
                        </div>
                    </div>
                    
                    <div className="border-t border-white/20 pt-6 text-center">
                        <p className="text-sm opacity-90 mb-4">&copy; 2025 KLINISOL. All rights reserved.</p>
                        
                        {/* Social Media Links */}
                        <div className="flex items-center justify-center gap-4">
                            <a 
                                href="https://www.linkedin.com/company/klinisol/" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="LinkedIn"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                            </a>
                            
                            <a 
                                href="https://youtube.com/@klinisol721" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all hover:scale-110"
                                aria-label="YouTube"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Contact;
